import { useEffect, useState, useMemo } from "react";
import { CircularProgress, Paper, Typography, Button, Stack} from "@mui/material";
import { useHistory, useParams } from "react-router";
import log from "loglevel";
import { Box } from "@mui/system";
import { Helmet } from "react-helmet";

import useFields, { FILTER_TYPES } from "../hooks/useFields";
import useEquipment from "../hooks/useEquipment";
import useUserPermissionsContext from "../hooks/useUserPermissionsContext";

import EditEntity from "../components/EditEntity";

import EntityOverview from "../views/EntityOverview";

import { addEquipment, patchEquipment } from "../dao/equipments";

import { softDelete } from "../dao/operations";

import { exportRecords } from "../dao/common";
import { exportToFile } from "../utils/utils";
import { errorMessageHandler } from "../utils/error";

import UserRights from "../enums/UserRights";
import ButtonWithSpinner from "../components/ButtonWithSpinner";
import MFilesButton from "../components/MFilesButton";
import CreateItemButton from "../components/CreateItemButton";
import EditModes from "../enums/EditModes";
import { ROUTES } from "../enums/Routes";
import { API_ROUTES } from "../enums/api";
import { useSearchParams } from "../hooks/useSearchParams";
import { addSearchParams } from "../utils/dao";
import { formatPageTitle } from "../utils/pageTitle";
import { makeFieldGroupsByLayout } from "../utils/form";
import { EQUIPMENT_RELATIONAL_FIELDS } from "../constants/softDelete";

const IRIS_TITLE = "Iris issues";
const REPORT_NAME = "Cables connected report";
const API_ROUTE = API_ROUTES["equipments"];

export default function EquipmentDetail({ title = "", mode = "" }) {
  const [isExporting, setIsExporting] = useState(false);
  const history = useHistory();

  // update record only
  // get equipmentId from the search params
  const { equipmentId } = useParams();

  // create record only
  // get fields from the search params
  const {
    objectId,
    systemGroupId,
    systemId,
    locationId,
    projectId,
    sectionReference,
  } = useSearchParams();

  const { isAllowedTo, isLoading } = useUserPermissionsContext();

  const isUpdateMode = mode === EditModes.UPDATE;
  const isCreateMode = mode === EditModes.CREATE;

  const isAllowedToCreate = isAllowedTo(
    UserRights.CREATE,
    FILTER_TYPES.equipment
  );

  const isAllowedToUpdate = isAllowedTo(
    UserRights.UPDATE,
    FILTER_TYPES.equipment
  );

  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.equipment
  );

  const {
    equipment,
    fetchEquipmentData,
    isLoading: recordIsLoading,
  } = useEquipment({
    objectId,
    systemGroupId,
    systemId,
    locationId,
    equipmentId,
    projectId,
    sectionReference,
  });

  const [fields] = useFields(
    FILTER_TYPES.equipment,
    false,
    isCreateMode,
    isUpdateMode
  );

  const searchParams = useMemo(() => {
    return new URLSearchParams(
      Object.entries({
        objectId: equipment.object_id,
        systemGroupId: equipment.system_group_id,
        systemId: equipment.system_id,
        locationId: equipment.location_id,
        projectId: equipment.project_id,
      }).filter(([, value]) => value)
    ).toString();
  }, [
    equipment.object_id,
    equipment.system_group_id,
    equipment.system_id,
    equipment.location_id,
    equipment.project_id,
  ]);
// set specific properties to layout the CableDetail view, in such a way that the generic
  // EditEntity component can be used, even when other detail views need different properties or no properties at all. 
  // Note: fields will be ordered according to the settings in the database, not the order here.

  const layoutProperties = useMemo(() => ({
    groups: [
      {
        name: "Engineering",
        title: "Engineering",
        fields: [
          { name: "project_id" },
          { name: "system_id" },
          { name: "equipment_type" },
          { name: "number" },
          { name: "description" },
          { name: "__placeholder__" },
          { name: "function" },
          { name: "location_id" },
          { name: "section_reference" },
          { name: "compartment" },
          { name: "export_to_iris" },
          { name: "eplan_synchronisation" },
          { name: "eplan_part" },
          { name: "eplan_part_symbol" },
          { name: "eplan_part_group_zone" },
          { name: "delivery_responsible" },
          { name: "installer" },
          { name: "manufacturer" },
          { name: "supplier" },
          { name: "supplier_reference" },
          { name: "client_reference" },
          { name: "owner_reference" },
          { name: "drawing" },
          { name: "notes" },
          { name: "width" },
          { name: "height" },
          { name: "depth" },
          { name: "weight" },
          { name: "heat_to_air" },
          { name: "contains_fluid_cooling_unit" },
          { name: "heat_to_fluid" },
          { name: "pressure_drop" },
          { name: "flow" },
          { name: "voltage" },
          { name: "supplied_from_reference" },
          { name: "power" },
          { name: "power_unit" },
          { name: "current" },
          { name: "current_unit" },
          { name: "cos_phi" },
          { name: "start_current" },
          { name: "start_current_unit" },

        ]
      },
      {
        name: "Installation",
        title: "Installation",
        fields: [
          { name: "final_location" },
          { name: "delivered" },
          { name: "installed" },
          { name: "on_hold" },
          { name: "on_hold_explanation" },
          { name: "spare"}
        ]
      },
      {
        name: "Revision",
        title: " ", // to avoid showing the title "Revision", but keep the spacing it would otherwise occupy
        align: "right",
        fields: [
          { name: "revision" },
          { name: "revision_date" },
        ]
      }         
    ]
  }), []);

  const sortFields = useMemo(
    () => (fields) => makeFieldGroupsByLayout(fields, layoutProperties),
    [layoutProperties]
  );

  const pageTitle = formatPageTitle(title);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function storeEquipment(payload) {
    if (isUpdateMode) {
      // Existing Equipment
      log.trace("about to patch", equipment.id);
      await patchEquipment(payload, equipment.id).then(() => {
        if (
          objectId !== payload.object_id ||
          systemId !== payload.system_id ||
          systemGroupId !== payload.system_group_id
        ) {
          history.push(["/equipment", equipment.id].join("/"));
        } else {
          fetchEquipmentData();
        }
      });
    } else {
      // Brand new Equipment
      log.trace("about to create", JSON.stringify(payload));
      return addEquipment(payload).then((res) => {
        history.push(["/equipment", res.id].join("/"));
      });
    }
  }

  async function removeEquipment(payload) {
    if (isUpdateMode) {
      return softDelete({
        route: API_ROUTE,
        delete: true,
        includeFields: EQUIPMENT_RELATIONAL_FIELDS,
      })(payload).then(() => {
        fetchEquipmentData();
      });
    }
  }

  async function cancelChanges() {
    if (isUpdateMode) {
      fetchEquipmentData();
    } else {
      history.goBack();
    }
  }

  function restoreDeletedEquipment(payload) {
    softDelete({
      route: API_ROUTE,
      delete: false,
      includeFields: EQUIPMENT_RELATIONAL_FIELDS,
    })(payload).then(() => {
      fetchEquipmentData();
    });
  }

  const handleExportCableListClick = () => {
    setIsExporting(true);
    const abortController = new AbortController();
    exportRecords(
      `/reports/cable`,
      {
        equipment_id: equipmentId,
      },
      abortController,
      "application/json"
    )
      .then((res) => {
        exportToFile(
          `${REPORT_NAME} - ${equipmentId}.pdf`,
          res,
          "application/pdf"
        );
      })
      .catch((err) => {
        errorMessageHandler(err);
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  return !fields || recordIsLoading ? (
    <Box
      sx={{
        marginTop: 3,
        textAlign: "center",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Paper
        sx={{
          padding: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <Typography variant='h5'>{title}</Typography>
          {isUpdateMode && (
             <Stack direction={"row"} spacing={1}>
              {/* Only show Iris link on edit screen and when we have an id */}
              {equipment.iris_id && (
                <Button
                  href={[
                    process.env.REACT_APP_IRIS_HOST,
                    "IRIS/relations",
                    equipment.iris_id,
                  ].join("/")}
                  title={IRIS_TITLE}
                  sx={{
                    padding: "4px 16px",
                  }}
                  variant='outlined'
                  target='_blank'
                >
                  {IRIS_TITLE}
                </Button>
              )}
              <MFilesButton hyperlink={equipment.m_files_hyperlink} />  
              <CreateItemButton
                size='medium'
                entity={FILTER_TYPES.equipment}
                to={addSearchParams(ROUTES.equipment.create)(searchParams)}
                isDisabled={isAllowedToCreate === false}
              />
            </Stack>
          )}
        </Box>
        {isLoading === false && (
          <>
            <EditEntity
              {...{
                isAllowedToUpdate,
                isAllowedToDelete,
                isAllowedToCreate,
                entity: equipment,
                fields,
                storeEntity: storeEquipment,
                deleteEntity: removeEquipment,
                entityType: FILTER_TYPES.equipment,
                cancel: cancelChanges,
                restoreEntity: restoreDeletedEquipment,
                isUpdateMode,
                sortFields
              }}
            />
            {isUpdateMode && (
              <>
                <ButtonWithSpinner
                  onClick={handleExportCableListClick}
                  variant='text'
                  color='primary'
                  title={`Export ${REPORT_NAME}`}
                  loading={isExporting}
                  // TODO: replace hard coded value with isExporting to enable the feature
                  disabled={true}
                >
                  {`Export ${REPORT_NAME}`}
                </ButtonWithSpinner>
                <EntityOverview
                  entity={FILTER_TYPES.section}
                  title={"Sections overview"}
                  nestedEntity={true}
                  parent={{
                    object_id: equipment.object_id,
                    system_group_id: equipment.system_group_id,
                    system_id: equipment.system_id,
                    equipment_id: equipment.id,
                  }}
                />
              </>
            )}
          </>
        )}
      </Paper>
    </>
  );
}
